<template>
    <div class="accordion" id="cases-settings-accordion">
        <nav id="settings" class="d-flex justify-content-between align-items-center mb-3">
            <!-- Navigation Tabs -->
            <a href="#settings" class="settings-option" :class="{ active: openTab === 'departments' }"
                @click.prevent="changeTab('departments')">
                <i class="fas fa-building"></i>
                <span class="title">{{ __('Departments') }}</span>
            </a>
            <a href="#settings" class="settings-option" :class="{ active: openTab === 'groups' }"
                @click.prevent="changeTab('groups')">
                <i class="fas fa-users"></i>
                <span class="title">{{ __('Groups') }}</span>
            </a>
            <a href="#settings" class="settings-option" :class="{ active: openTab === 'settings' }"
                @click.prevent="changeTab('settings')">
                <i class="fas fa-cogs"></i>
                <span class="title">{{ __('Settings') }}</span>
            </a>
        </nav>

        <!-- Departments Tab -->
        <div class="card" v-if="openTab === 'departments'">
            <div class="card-header">
                <h5 class="header-title">{{ __('Departments') }}</h5>
            </div>
            <div class="card-body">
                <div class="row">
                    <div class="col-md-6 col-sm-12 mb-5 mb-md-0">
                        <!-- Form Title -->
                        <h5>{{ editingDepartmentIndex === null ? __('Create Department') : __('Edit Department') }}</h5>

                        <!-- Status Message -->
                        <div v-if="statusMessage.message" :class="`alert alert-${statusMessage.type} mt-3`"
                            role="alert">
                            {{ statusMessage.message }}
                        </div>

                        <!-- Group Form -->
                        <form>
                            <!-- Group Name Input -->
                            <div class="form-group">
                                <label for="departmentName">{{ __('Department Name') }}</label>
                                <input id="departmentName" type="text" class="form-control" v-model="currentDepartment.name"
                                    :placeholder="__('Enter department name')" />
                            </div>
                            <!-- Submit Buttons -->
                            <button @click.prevent="saveDepartment()" class="btn btn-primary" :disabled="!currentDepartment.name">
                                {{ editingDepartmentIndex === null ? __('Create Department') : __('Update Department') }}
                            </button>
                            <button v-if="editingDepartmentIndex !== null" class="btn btn-secondary"
                                @click.prevent="resetForm">
                                {{ __('Cancel Edit') }}
                            </button>
                        </form>
                    </div>

                    <!-- Existing Departments Section -->
                    <div class="col-md-6 col-sm-12">
                        <h5>{{ __('Existing Departments') }}</h5>
                        <div v-if="departments.length === 0" class="alert alert-info">
                            {{ __('No departments to show') }}
                        </div>
                        <!-- List of Departments -->
                        <ul class="list-group" v-else>
                            <li v-for="(department, index) in departments" :key="departments.id"
                                :class="['list-group-item', { 'editing-group': editingDepartmentIndex === index }]">
                                <div class="row">
                                    <!-- Department name -->
                                    <div class="col-12">
                                        <strong>{{ labelizeGeneral(department.name) }}</strong>
                                    </div>
                                </div>
                                <div class="row mt-2 justify-content-between">
                                    <!-- Edit and Delete Buttons -->
                                    <div class="col-auto">
                                        <button class="btn btn-primary btn-md" @click.prevent="editDepartment(index)">
                                            <i class="fa fa-edit"></i> {{ __('Edit') }}
                                        </button>
                                    </div>
                                    <div class="col-auto">
                                        <button class="btn btn-danger btn-md" @click.prevent="deleteDepartment(index)">
                                            <i class="fa fa-trash"></i> {{ __('Delete') }}
                                        </button>
                                    </div>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>

        <!-- Groups Tab -->
        <div class="card" v-if="openTab === 'groups'">
            <div class="card-header">
                <h5 class="header-title">{{ __('Groups') }}</h5>
            </div>
            <div class="card-body">
                <div class="row">
                    <div class="col-md-6 col-sm-12 mb-5 mb-md-0">
                        <!-- Form Title -->
                        <h5>{{ editingGroupIndex === null ? __('Create Group') : __('Edit Group') }}</h5>

                        <!-- Status Message -->
                        <div v-if="statusMessage.message" :class="`alert alert-${statusMessage.type} mt-3`"
                            role="alert">
                            {{ statusMessage.message }}
                        </div>

                        <!-- Group Form -->
                        <form>
                            <!-- Group Name Input -->
                            <div class="form-group">
                                <label for="groupName">{{ __('Group Name') }}</label>
                                <input id="groupName" type="text" class="form-control" v-model="currentGroup.name"
                                    :placeholder="__('Enter group name')" />
                            </div>
                            <!-- Group Users Selector -->
                            <div class="form-group">
                                <label for="groupUsers">{{ __('Add Users to Group') }}</label>
                                <AssigneesSelector id="groupUsers" v-model="currentGroup.users"
                                    :users="spark.state.currentTeam.users" :allow-emails="true" :allow-groups="false"
                                    :allow-users="true" :placeholder="__('Add users to group')"
                                    @message="setStatusMessage" />
                            </div>
                            <!-- Submit Buttons -->
                            <button class="btn btn-primary"
                                @click.prevent="editingGroupIndex === null ? createGroup() : updateGroup()"
                                :disabled="!(currentGroup.name && currentGroup.users.length > 0)">
                                {{ editingGroupIndex === null ? __('Create Group') : __('Update Group') }}
                            </button>
                            <button v-if="editingGroupIndex !== null" class="btn btn-secondary"
                                @click.prevent="resetForm">
                                {{ __('Cancel Edit') }}
                            </button>
                        </form>
                    </div>

                    <!-- Existing Groups Section -->
                    <div class="col-md-6 col-sm-12">
                        <h5>{{ __('Existing Groups') }}</h5>
                        <div v-if="groups.length === 0" class="alert alert-info">
                            {{ __('No groups to show') }}
                        </div>
                        <!-- List of Groups -->
                        <ul class="list-group" v-else>
                            <li v-for="(group, index) in groups" :key="group.id"
                                :class="['list-group-item', { 'editing-group': editingGroupIndex === index }]">
                                <div class="row">
                                    <!-- Group Name and Users -->
                                    <div class="col-12">
                                        <strong>{{ group.name }}</strong>
                                        <br />
                                        <small class="text-muted">
                                            {{ group.users.map((user) => user.label).join(', ') }}
                                        </small>
                                    </div>
                                </div>
                                <div class="row mt-2 justify-content-between">
                                    <!-- Edit and Delete Buttons -->
                                    <div class="col-auto">
                                        <button class="btn btn-primary btn-md" @click.prevent="editGroup(index)">
                                            <i class="fa fa-edit"></i> {{ __('Edit') }}
                                        </button>
                                    </div>
                                    <div class="col-auto">
                                        <button class="btn btn-danger btn-md" @click.prevent="deleteGroup(index)">
                                            <i class="fa fa-trash"></i> {{ __('Delete') }}
                                        </button>
                                    </div>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>

        <!-- Settings Tab -->
        <div class="card" v-if="openTab === 'settings'">
            <div class="card-header">
                <h5 class="header-title">{{ __('Settings') }}</h5>
            </div>
            <div class="card-body">
                <div class="row">
                    <div class="col-md-6 col-sm-12">
                        <!-- Default Settings Form -->
                        <h5>{{ __('Default Settings') }}</h5>

                        <!-- Status Message -->
                        <div v-if="statusMessage.message" :class="`alert alert-${statusMessage.type} mt-3`"
                            role="alert">
                            {{ statusMessage.message }}
                        </div>

                        <form>
                            <!-- Default Assignees Selector -->
                            <div class="form-group">
                                <label for="defaultAssignees">{{ __('Default Assignees') }}</label>
                                <AssigneesSelector id="defaultAssignees" v-model="defaults.assignees"
                                    :users="spark.state.currentTeam.users" :groups="groups" :allow-emails="true"
                                    :allow-groups="true" :allow-users="true" :placeholder="__('Add default assignees')"
                                    @message="setStatusMessage" />
                            </div>
                            <!-- Default Priority Dropdown -->
                            <div class="form-group">
                                <label for="defaultPriority">{{ __('Default Priority') }}</label>
                                <select id="defaultPriority" class="form-control" v-model="defaults.priority">
                                    <option value="0">{{ __('Low') }}</option>
                                    <option value="1">{{ __('Normal') }}</option>
                                    <option value="2">{{ __('High') }}</option>
                                </select>
                            </div>
                            <!-- Save Defaults Button -->
                            <button class="btn btn-primary" @click.prevent="updateDefaults">{{ __('Save Defaults')
                                }}</button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>


<script>
import CaseService from '@/services/cases-service';
import AssigneesSelector from '@/components/cases/AssigneesSelector.vue';

export default {
    components: {
        AssigneesSelector,
    },
    data() {
        return {
            openTab: 'departments', // Tracks the active tab (either 'groups' or 'settings' or 'departments')
            currentGroup: {    // Holds the data for the group currently being created/edited
                name: '',
                users: [],
            },
            currentDepartment: {
                name: '',
                // ...
            },
            groups: [],        // List of all existing groups
            departments: [],        // List of all existing groups
            defaults: {        // Default settings for groups
                priority: '1', // Default priority level (1 = Normal)
                assignees: [], // Default list of assignees
            },
            statusMessage: {   // Status message for user feedback
                message: '',   // Message content
                type: '',      // Type of message (success, error, etc.)
            },
            editingGroupIndex: null, // Index of the group being edited; null if creating a new group
            editingDepartmentIndex: null, // Index of the department being edited; null if creating a new department
        };
    },

    async mounted() {
        this.$eventHub.$on('activateTab', this.handleTabActivation);
    },

    beforeDestroy() {
        this.$eventHub.$off('activateTab', this.handleTabActivation);
    },

    methods: {

        async handleTabActivation(tabId) {
            if (tabId === 'case-settings') {
                this.fetchSettings();
            }
        },

        // Fetch group and default settings from the server
        async fetchSettings() {
            try {
                const data = await CaseService.getSettings(Spark.state.currentTeam.id);

                // Map group data to the format used by the frontend
                this.groups = data.groups.map((group) => ({
                    ...group,
                    users: this.transformUsers(group.users),
                }));

                this.departments = data.departments;

                // Set default settings
                this.defaults = {
                    priority: data.defaults?.priority.toString() || '1',
                    assignees: this.transformDefaults(data.defaults?.assignees) || [],
                };
            } catch (error) {
                console.error('Error fetching settings:', error);
                this.setStatusMessage('Failed to fetch settings.', 'danger');
            }
        },

        // Update default settings on the server
        async updateDefaults() {
            try {
                const payload = {
                    priority: parseInt(this.defaults.priority, 10),
                    assignees: this.transformPayload(this.defaults.assignees),
                };
                await CaseService.updateDefaults(Spark.state.currentTeam.id, payload);
                this.setStatusMessage('Defaults saved successfully.', 'success');
            } catch (error) {
                console.error('Error updating defaults:', error);
                this.setStatusMessage('Failed to save defaults.', 'danger');
            }
        },

        // Transform frontend payload to match backend requirements
        transformPayload(payload) {
            return payload.map((assignee) => {
                if (assignee.key.type === 'group') {
                    return { group_id: assignee.key.value };
                } else if (assignee.key.type === 'user') {
                    return { user_id: assignee.key.value };
                } else if (assignee.key.type === 'email') {
                    return { email: assignee.key.value };
                }
                return null;
            });
        },

        // Transform backend defaults to match frontend requirements
        transformDefaults(defaults) {
            if (!defaults) return [];
            return defaults.map((assignee) => {
                if (assignee.group_id) {
                    const group = this.groups.find((g) => g.id === assignee.group_id);
                    return { key: { type: 'group', value: group.id }, label: group.name };
                } else if (assignee.user_id) {
                    const user = Spark.state.currentTeam.users.find((u) => u.id === assignee.user_id);
                    if (!user) return null; // Skip if the user is not found
                    return {
                        key: { type: 'user', value: user.id, email: user.email },
                        label: `${user.name} (${user.email})`,
                    };
                } else if (assignee.email) {
                    return { key: { type: 'email', value: assignee.email }, label: assignee.email };
                }
                return null;
            }).filter((assignee) => assignee !== null); // Remove null values
        },

        // Transform group users to match frontend format
        transformUsers(users) {
            return users.map((user) => {
                if (user.user_id) {
                    const foundUser = Spark.state.currentTeam.users.find((u) => u.id === user.user_id);
                    return {
                        id: user.user_id,
                        key: { type: 'user', value: foundUser.id, email: foundUser.email },
                        label: `${foundUser.name} (${foundUser.email})`,
                    };
                } else if (user.email) {
                    return { key: { type: 'email', value: user.email }, label: user.email };
                }
            });
        },

        // Display a status message to the user
        setStatusMessage(message, type) {
            this.statusMessage = { message, type };
            // Clear the message after 5 seconds
            setTimeout(() => {
                this.statusMessage.message = '';
            }, 5000);
        },

        // Reset the form to default values
        resetForm() {
            this.currentGroup = { name: '', users: [] };
            this.currentDepartment = { name: '' };
            this.editingGroupIndex = null;
            this.editingDepartmentIndex = null;
        },

        // Change the active tab
        changeTab(tab) {
            this.openTab = tab;
        },

        async saveDepartment() {
            try {
                
                const isEditing = this.editingDepartmentIndex !== null;
                const departmentId = isEditing ? this.departments[this.editingDepartmentIndex].id : null;
                const data = {
                    name: this.currentDepartment.name,
                };
                const response = await CaseService.saveDepartment(Spark.state.currentTeam.id, data, departmentId);

                if (isEditing) {
                    this.departments[this.editingDepartmentIndex] = response.department;
                } else {
                    this.departments.push({
                        id: response.department.id,
                        name: response.department.name,
                    });
                }

                this.resetForm();
                this.setStatusMessage(isEditing ? __('Department updated successfully.') : __('Department created successfully.'), 'success');
            } catch (error) {
                this.setStatusMessage(error.message || __('Failed to save department.'), 'danger');
            }
        },

        // Delete a Department
        async deleteDepartment(index) {
            const departmentId = this.departments[index].id;
            try {
                const response = await CaseService.deleteDepartment(Spark.state.currentTeam.id, departmentId);
                this.departments.splice(index, 1); // Remove the department from the list
                this.setStatusMessage("Department deleted successfully.", "success");
            } catch (error) {
                console.error("Error deleting department:", error);
                this.setStatusMessage("Failed to delete department.", "danger");
            }        
        },

        // Edit an existing department
        async editDepartment(index) {
            this.departments.forEach((department) => (department.isActive = false)); 
            this.departments[index].isActive = true; 
            this.currentDepartment = {
                name: this.departments[index].name,
            };
            this.editingDepartmentIndex = index;      
        },

        // Create a new group
        async createGroup() {
            try {
                const groupToCreate = {
                    name: this.currentGroup.name,
                    users: this.transformPayload(this.currentGroup.users),
                };
                const response = await CaseService.createGroup(Spark.state.currentTeam.id, groupToCreate);
                const createdGroup = response.group;
                // Add the new group to the list
                this.groups.push({
                    id: createdGroup.id,
                    name: createdGroup.name,
                    users: this.transformUsers(createdGroup.users),
                });
                this.resetForm();
                this.setStatusMessage(__('Group created successfully.'), 'success');
            } catch (error) {
                console.error('Error creating group:', error);
                this.setStatusMessage(error.message || __('Failed to create group.'), 'danger');
            }
        },

        // Update an existing group
        async updateGroup() {
            try {
                const groupId = this.groups[this.editingGroupIndex].id;
                const groupToUpdate = {
                    name: this.currentGroup.name,
                    users: this.transformPayload(this.currentGroup.users),
                };
                const response = await CaseService.updateGroup(Spark.state.currentTeam.id, groupId, groupToUpdate);
                const updatedGroup = response.group;
                // Update the group in the list
                this.groups[this.editingGroupIndex] = {
                    ...this.groups[this.editingGroupIndex],
                    name: updatedGroup.name,
                    users: this.transformUsers(updatedGroup.users),
                };
                this.resetForm();
                this.setStatusMessage(__('Group updated successfully.'), 'success');
            } catch (error) {
                console.error('Error updating group:', error);
                this.setStatusMessage(error.message || __('Failed to update group.'), 'danger');
            }
        },

        // Delete a group
        async deleteGroup(index) {
            const groupId = this.groups[index].id;
            try {
                const response = await CaseService.deleteGroup(Spark.state.currentTeam.id, groupId);
                this.groups.splice(index, 1); // Remove the group from the list
                this.defaults.assignees = this.defaults.assignees.filter(
                    (assignee) => assignee.key.type !== 'group' || assignee.key.value !== groupId
                );
                this.setStatusMessage("Group deleted successfully.", "success");
            } catch (error) {
                console.error("Error deleting group:", error);
                this.setStatusMessage("Failed to delete group.", "danger");
            }
        },

        // Edit an existing group
        async editGroup(index) {
            this.groups.forEach((group) => (group.isActive = false)); // Reset active state for all groups
            this.groups[index].isActive = true; // Mark the selected group as active
            // Populate the form with the selected group's data
            this.currentGroup = {
                name: this.groups[index].name,
                users: this.groups[index].users,
            };
            this.editingGroupIndex = index; // Set the index for editing
        },
    },
};
</script>

<style scoped>
.editing-group {
    border: 2px solid #007bff;
    /* Blue border or any color you prefer */
    background-color: #f0f8ff;
    /* Light blue background for better contrast */
    box-shadow: 0 0 10px rgba(0, 123, 255, 0.3);
    /* Light shadow for emphasis */
}

.editing-group strong {
    color: #0056b3;
    /* Darker text for the group name when editing */
}
</style>